/**
 * read-window.ts: the per-source freshness window of the offers read path,
 * mirrored from the SQL function `offer_row_is_fresh(source, last_verified_at)`
 * (supabase/migrations/0045_offers_feed_and_snapshots.sql, spec
 * docs/OFFERS_ENGINE_V2.md sections 1.4 and 2.4).
 *
 * The SQL function is the truth: `get_category_products/3` and
 * `getOffersByGroup` both filter through it. This module exists for the two
 * readers that cannot call SQL, the Realtime converter in the browser
 * (frontend/hooks/use-live-products.ts) and the box's own cache-hit read, so a
 * live arrival can never show a page a row the SQL would hide.
 *
 * The windows are correctness rules, not cadence knobs, which is why they are
 * constants here and in SQL rather than rows in a settings table:
 *   feed          48 h   a feed is re-imported daily; two missed imports is a
 *                        dead item (R65)
 *   creators-api  24 h   Amazon's cache rule (constraint 4, R76)
 *   everything    30 d   a page read is trusted for a month, as since 0034
 * All on `last_verified_at`, the SUCCESS clock (a price was read).
 *
 * PURE: no clock of its own (`now` is a parameter), no imports.
 */

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/** How long a row stays in the panel after its last successful read. */
export const OFFER_READ_WINDOW_MS = Object.freeze({
  feed: 48 * HOUR_MS,
  "creators-api": 24 * HOUR_MS,
  default: 30 * DAY_MS,
});

/** The window for one source, in milliseconds. */
export function offerReadWindowMs(source: string | null | undefined): number {
  if (source === "feed") return OFFER_READ_WINDOW_MS.feed;
  if (source === "creators-api") return OFFER_READ_WINDOW_MS["creators-api"];
  return OFFER_READ_WINDOW_MS.default;
}

/**
 * `offer_row_is_fresh` in TypeScript: false for a missing or unparseable
 * success clock, true while `now - lastVerifiedAt` is inside the source's
 * window. A clock in the future counts as fresh, as it does in SQL
 * (`>= now() - interval`).
 */
export function isOfferRowFresh(
  source: string | null | undefined,
  lastVerifiedAt: string | number | Date | null | undefined,
  now: number = Date.now(),
): boolean {
  if (lastVerifiedAt === null || lastVerifiedAt === undefined || lastVerifiedAt === "") return false;
  const verifiedMs =
    lastVerifiedAt instanceof Date
      ? lastVerifiedAt.getTime()
      : typeof lastVerifiedAt === "number"
        ? lastVerifiedAt
        : Date.parse(lastVerifiedAt);
  if (!Number.isFinite(verifiedMs)) return false;
  return now - verifiedMs <= offerReadWindowMs(source);
}
